import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { processSquarePayment, formatPrice } from '../services/bookingService';

const RawSquarePaymentForm = ({
  clinicId,
  serviceId,
  practitionerId,
  amount,
  patientEmail,
  patientName,
  appointmentDetails,
  onSuccess,
  onCancel,
  returnUrl,
  squareConfig, // { applicationId, locationId }
}) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [card, setCard] = useState(null);
  const [payments, setPayments] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const cardRef = useRef(null);
  const mountedRef = useRef(true);

  console.log('Raw Square Config received:', squareConfig);

  // Load Square SDK script
  useEffect(() => {
    const loadSquareSDK = () => {
      return new Promise((resolve, reject) => {
        // Check if already loaded
        if (window.Square) {
          console.log('Square SDK already loaded');
          resolve();
          return;
        }

        // Load Square SDK script
        const script = document.createElement('script');
        script.src = 'https://sandbox.web.squarecdn.com/v1/square.js';
        script.async = true;
        script.onload = () => {
          console.log('Square SDK script loaded');
          resolve();
        };
        script.onerror = () => {
          console.error('Failed to load Square SDK script');
          reject(new Error('Failed to load Square SDK'));
        };
        document.head.appendChild(script);
      });
    };

    loadSquareSDK().catch((error) => {
      console.error('Error loading Square SDK:', error);
      setError('Failed to load payment system. Please try again.');
    });
  }, []);

  // Component mount tracking
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Initialize Square SDK with better DOM isolation
  useEffect(() => {
    if (!squareConfig?.applicationId || !squareConfig?.locationId) {
      console.log('Missing Square configuration');
      return;
    }

    // Prevent multiple initializations
    if (isInitialized) {
      console.log('Square SDK already initialized, skipping...');
      return;
    }

    let cardInstance = null;
    let paymentsInstance = null;

    const initializeSquare = async () => {
      try {
        console.log('Initializing Raw Square SDK...');
        
        // Wait for Square SDK to load
        if (!window.Square) {
          console.log('Square SDK not loaded yet, waiting...');
          // Try again in a bit
          if (mountedRef.current) {
            setTimeout(initializeSquare, 500);
          }
          return;
        }

        // Check if component is still mounted
        if (!mountedRef.current || !cardRef.current) {
          console.log('Component unmounted during initialization');
          return;
        }

        // Create a completely isolated container
        const cardContainer = document.createElement('div');
        cardContainer.style.cssText = `
          width: 100%;
          min-width: 480px;
          min-height: 120px;
          padding: 0;
          margin: 0;
          border: none;
          background: transparent;
        `;
        
        // Clear and append isolated container
        cardRef.current.innerHTML = '';
        cardRef.current.appendChild(cardContainer);

        // Initialize payments object
        paymentsInstance = window.Square.payments(squareConfig.applicationId, squareConfig.locationId);
        console.log('Square payments instance created');
        
        if (!mountedRef.current) return;
        setPayments(paymentsInstance);

        // Initialize card with minimal styling
        cardInstance = await paymentsInstance.card();
        console.log('Square card instance created');
        
        if (!mountedRef.current) return;
        setCard(cardInstance);

        // Attach card to isolated DOM container
        if (cardContainer && mountedRef.current) {
          await cardInstance.attach(cardContainer);
          console.log('Square card attached to isolated DOM container');
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Error initializing Square SDK:', error);
        if (mountedRef.current) {
          setError('Failed to initialize payment form. Please try again.');
        }
      }
    };

    // Add a small delay to ensure DOM is ready
    const timer = setTimeout(initializeSquare, 100);

    // Cleanup with better isolation
    return () => {
      clearTimeout(timer);
      mountedRef.current = false;
      
      if (cardInstance) {
        try {
          console.log('Destroying Square card instance...');
          cardInstance.destroy();
          console.log('Square card destroyed successfully');
        } catch (e) {
          console.log('Error destroying card (safe to ignore):', e);
        }
      }
      
      // Clear the container manually to prevent React conflicts
      if (cardRef.current) {
        try {
          cardRef.current.innerHTML = '';
        } catch (e) {
          console.log('Error clearing container (safe to ignore):', e);
        }
      }
      
      setCard(null);
      setPayments(null);
      setIsInitialized(false);
    };
  }, [squareConfig?.applicationId, squareConfig?.locationId]);

  // Handle payment submission
  const handlePayment = async () => {
    if (!card) {
      setError('Payment form not ready. Please try again.');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      console.log('Tokenizing card...');
      const tokenResult = await card.tokenize();
      
      if (tokenResult.status === 'OK') {
        console.log('Payment token received:', tokenResult.token);
        
        // Extract the clinic stub from the URL
        const pathParts = window.location.pathname.split('/');
        const clinicStub = pathParts[1];

        // Prepare payment data with the token
        const paymentData = {
          token: tokenResult.token,
          amount: amount,
          currency: 'AUD',
          clinic_id: clinicId,
          service_id: serviceId,
          practitioner_id: practitionerId,
          patient_email: patientEmail,
          patient_name: patientName,
          appointment_details: appointmentDetails,
          clinic_stub: clinicStub,
          return_url: returnUrl
        };

        // Process payment through backend
        const result = await processSquarePayment(paymentData);
        
        if (result.success) {
          console.log('Payment successful:', result);
          onSuccess(result);
        } else {
          throw new Error(result.error || 'Payment failed');
        }
      } else {
        console.error('Tokenization failed:', tokenResult.errors);
        throw new Error('Failed to process card details. Please check your information.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={onCancel}
          sx={{ mr: 2 }}
          disabled={processing}
        >
          Back
        </Button>
        <Typography variant="h6">
          Payment - Raw Square SDK
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Enter Payment Details
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Amount: {formatPrice(amount)} | Raw Square Web Payments SDK
        </Typography>

        {!squareConfig?.applicationId || !squareConfig?.locationId ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Square payment configuration is incomplete. 
              {!squareConfig?.applicationId && ' Missing Application ID.'}
              {!squareConfig?.locationId && ' Missing Location ID.'}
            </Alert>
            <Button variant="outlined" onClick={onCancel}>
              Go Back
            </Button>
          </Box>
        ) : processing ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Processing payment...
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Card Details
              </Typography>
              
              {/* Raw Square Card Element - Isolated from React */}
              <div 
                ref={cardRef} 
                id="sq-card-container"
                suppressHydrationWarning={true}
                style={{
                  minHeight: '120px',
                  width: '100%',
                  minWidth: '480px', // Force single-line layout
                  border: '1px solid #E0E6ED',
                  borderRadius: '6px',
                  padding: '16px',
                  backgroundColor: 'white',
                  position: 'relative'
                }}
              >
                {/* Loading state - will be replaced by Square SDK */}
                {!isInitialized && (
                  <div style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading payment form...
                    </Typography>
                  </div>
                )}
              </div>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button 
                variant="outlined"
                onClick={onCancel}
                disabled={processing}
              >
                Cancel
              </Button>
              
              <Button 
                variant="contained"
                size="large"
                disabled={processing || !card}
                onClick={handlePayment}
                sx={{ minWidth: 120 }}
              >
                Pay {formatPrice(amount)}
                {processing && <CircularProgress size={20} sx={{ ml: 1 }} />}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RawSquarePaymentForm;